// aichat.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './AiChat.css';
import { buildSystemPrompt } from './buildSystemPrompt';


const AiChat = () => {
  const [datasets, setDatasets] = useState({
    cryptoAnalysis: [],
    aitriggered: [],
    aiCompleted: [],
    aiinactive: [],
  });
  const [searchInput, setSearchInput] = useState('');
  const [selectedToken, setSelectedToken] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const chatEndRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [aiTyping, setAiTyping] = useState(false);
  const [cachedPrompt, setCachedPrompt] = useState(null);
  const [cachedContext, setCachedContext] = useState(null); 


  const isLocal = window.location.hostname === 'localhost';
  const baseUrl = isLocal
    ? 'http://localhost:5000'
    : 'https://www.trendscout.club';

  // Auto-scroll
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatHistory]);

  // Fetch tokens from all datasets
  useEffect(() => {
    const fetchTokens = async () => {
      const allDatasets = ['cryptoAnalysis', 'aitriggered', 'aiCompleted', 'aiinactive'];
      const results = {};

      for (const dataset of allDatasets) {
        const res = await axios.get(`${baseUrl}/api/get-tokens-from-dataset?dataset=${dataset}`);
        results[dataset] = res.data || [];
      }

      setDatasets(results);
    };
    fetchTokens();
  }, []);

const handleTokenSelect = async (token) => {
  setSelectedToken(token);
  setLoading(true);

  try {
    const dbRes = await axios.get(`${baseUrl}/api/get-token?symbol=${token}`);
    const tokenData = dbRes.data;

    const gptRes = await axios.post(`${baseUrl}/api/analyze-token/aichat`, { symbol: token });
    const aiAnalysis = gptRes.data;

    // ✅ Extract fields from fresh analysis
    const {
      token: freshToken,
      priceAtAnalysis,
      gain,
      recommendation,
      tradeType,
      confidenceLevel,
      trendStrength,
      rsi,
      macd,
      volume,
      localSupport,
      localResistance,
      upsideTarget,
      upsideChance,
      downsideRisk,
      downsideChance,
      catalysts,
      justification,
      marketContext,
    } = aiAnalysis;

    // ✅ Prepare past fields from DB
    const pastFields = {
      pastToken: tokenData?.token || null,
      pastPriceAtAnalysis: tokenData?.priceAtAnalysis || null,
      pastGain: tokenData?.gain || null,
      pastRecommendation: tokenData?.recommendation || null,
      pastTradeType: tokenData?.tradeType || null,
      pastConfidenceLevel: tokenData?.confidenceLevel || null,
      pastTrendStrength: tokenData?.trendStrength || null,
      pastRsi: tokenData?.rsi || null,
      pastMacd: tokenData?.macd || null,
      pastVolume: tokenData?.volume || null,
      pastLocalSupport: tokenData?.localSupport || null,
      pastLocalResistance: tokenData?.localResistance || null,
      pastUpsideTarget: tokenData?.upsideTarget || null,
      pastUpsideChance: tokenData?.upsideChance || null,
      pastDownsideRisk: tokenData?.downsideRisk || null,
      pastDownsideChance: tokenData?.downsideChance || null,
      pastCatalysts: tokenData?.catalysts || null,
      pastJustification: tokenData?.justification || null,
      pastMarketContext: tokenData?.marketContext || null,
      pastOutcome: tokenData?.outcome || null,
      pastFeedbackSource: tokenData?.feedbackSource || null,
      pastHumanLabel: tokenData?.humanLabel || null,
      pastNotes: tokenData?.notes || null,
      pastAiReflection: tokenData?.aiReflection || null,
    };

    // ✅ Merge everything into single result for AI prompt
    const mergedResult = {
      token: freshToken,
      priceAtAnalysis,
      gain,
      recommendation,
      tradeType,
      confidenceLevel,
      trendStrength,
      rsi,
      macd,
      volume,
      localSupport,
      localResistance,
      upsideTarget,
      upsideChance,
      downsideRisk,
      downsideChance,
      catalysts,
      justification,
      marketContext,
      ...pastFields,
    };

    const prompt = buildSystemPrompt(mergedResult); // ✅ contains both fresh and past

    // Update state for chat
    setTokenData(tokenData);
    setAiAnalysis(aiAnalysis);
    setCachedPrompt(prompt);
    setCachedContext(mergedResult); // ✅ this is now cached
    setChatHistory([
      {
        role: 'system',
        content: `You are now analyzing ${token}.`,
      },
      {
        role: 'assistant',
        content: `I'm ready to assist with ${token}. Ask about entry points, catalysts, or setup ideas.`,
      },
    ]);
  } catch (err) {
    console.error(err);
    setChatHistory([{ role: 'assistant', content: `Error loading data for ${token}.` }]);
  } finally {
    setLoading(false);
  }
};



const handleSearch = async () => {
  const formatted = searchInput.toUpperCase().includes('/USDT')
    ? searchInput.toUpperCase()
    : `${searchInput.toUpperCase()}/USDT`;

  try {
    const res = await axios.get(`${baseUrl}/api/get-token?symbol=${formatted}`);
    if (res.data) {
      handleTokenSelect(res.data.token);
    } else {
      setChatHistory([{ role: 'assistant', content: 'Token not found in database.' }]);
    }
  } catch {
    setChatHistory([{ role: 'assistant', content: 'Token not found or error occurred.' }]);
  }
};


const handleSendMessage = async () => {
  if (!chatInput.trim()) return;

  const userMsg = { role: 'user', content: chatInput };
  const updatedHistory = [...chatHistory, userMsg];

  setChatHistory(updatedHistory);
  setChatInput('');
  setAiTyping(true);

  setTimeout(async () => {
    try {
      const messages = [
        { role: 'system', content: cachedPrompt },
        ...updatedHistory,
        { role: 'user', content: chatInput }
      ];

      const res = await axios.post(`${baseUrl}/api/chat-with-ai`, {
        messages,
        context: cachedContext, // ✅ send context to backend
      });

      const assistantReply = res.data.reply || 'Sorry, I couldn’t find an answer.';
      const updatedFields = res.data.updatedFields || {}; // ✅ AI-added context fields

      const delay = Math.max(1000, Math.min(3000, assistantReply.length * 20));

      setTimeout(() => {
        // ✅ Merge new AI fields into context if present
        if (Object.keys(updatedFields).length > 0) {
          const newContext = {
            ...cachedContext,
            ...updatedFields,
          };
          setCachedContext(newContext);
        }

        setChatHistory(prev => [...prev, { role: 'assistant', content: assistantReply }]);
        setAiTyping(false);
      }, delay);
    } catch (err) {
      console.error(err);
      setChatHistory(prev => [...prev, {
        role: 'assistant',
        content: 'Something went wrong while trying to answer.',
      }]);
      setAiTyping(false);
    }
  }, 100);
};



  return (
<div className="ai-chat-container">
  <div className="aichat-left-panel">
    <h2>AI Token Chat</h2>

    <div className="search-box-aichat">
      <input
        type="text"
        value={searchInput}
        placeholder="Search token (e.g. ETH)"
        onChange={(e) => setSearchInput(e.target.value)}
      />
      <button onClick={handleSearch}>Search</button>
    </div>

    <div className="aichat-dropdowns">
      {Object.entries(datasets).map(([dataset, tokens]) => (
        <div key={dataset}>
          <h4>{dataset}</h4>
          <select className="aichat-select" onChange={(e) => handleTokenSelect(e.target.value)}>
            <option value="">Select token</option>
            {tokens.map((token, i) => (
              <option key={i} value={token}>{token}</option>
            ))}
          </select>
        </div>
      ))}
    </div>

    {selectedToken && (
      <div className="token-header">
        <div className="token-info">
          <img
            src={`https://cryptologos.cc/logos/${selectedToken.toLowerCase().replace('/usdt', '')}-logo.png?v=010`}
            alt={selectedToken}
            onError={(e) => (e.target.style.display = "none")}
            className="token-avatar"
          />
          <h3>{selectedToken}</h3>
        </div>
        <iframe
          src={`https://www.tradingview.com/widgetembed/?symbol=BINANCE:${selectedToken.replace('/USDT', 'USDT')}&interval=60&hidesidetoolbar=1&hideideas=1&theme=dark`}
          frameBorder="0"
          width="100%"
          height="350"
          className="chart-preview"
        />
      </div>
    )}
  </div>

  <div className="aichat-right-panel">
    <div className="chat-window">
      {chatHistory.map((msg, idx) => (
        <div key={idx} className={`chat-msg ${msg.role}`}>
          <strong>{msg.role === 'user' ? 'You' : 'AI'}:</strong>
          <div className="chat-content">{msg.content}</div>
          <div className="chat-timestamp">{new Date().toLocaleTimeString()}</div>
        </div>
      ))}

      {aiTyping && (
        <div className="chat-msg assistant">
          <strong>AI:</strong>
          <div className="chat-content typing-indicator">
            <span>.</span><span>.</span><span>.</span>
          </div>
        </div>
      )}
      <div ref={chatEndRef} />
    </div>

    <div className="chat-input">
      <textarea
        rows="2"
        placeholder="Ask something about the token..."
        value={chatInput}
        onChange={(e) => setChatInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
          }
        }}
      />
      <button onClick={handleSendMessage}>Send</button>
    </div>
  </div>

  {loading && (
    <div className="modalloading-overlay">
      <div className="modalloading">
        <h3>Analyzing Token...</h3>
        <div className="loader"></div>
        <p>Please wait while we retrieve data and generate analysis.</p>
      </div>
    </div>
  )}
</div>



  );
};

export default AiChat;
